import { createFeature, createReducer, on } from '@ngrx/store';

import { RpmItem, RpmPersonData } from '@core/models';
import { RpmApiActions, RpmPageActions } from './rpm.actions';

/**
 * `rpm` feature slice — each person's RPM data as the SERVER last answered it,
 * keyed by persona id. The company colour palette its categories are painted
 * from is app-wide reference data (`selectPalette` in the reference slice).
 *
 * Every write lands here only as the server's ANSWER (an `RpmApiActions` case),
 * never optimistically, so what is on screen is what was accepted. Overdue,
 * estimates, a week's outcomes and counts and the year summary are the server's
 * too; nothing here works one out.
 *
 * `saving` is true from the moment a write is dispatched until its answer or
 * refusal arrives — what the modals' Save buttons and the one-click controls
 * wait on (standards §8h). One flag, because the effects run writes one at a
 * time (`concatMap`) and one form is open at a time.
 */
export interface RpmState {
  /** Persona id → the live plan, categories, Grass Catcher, the week being looked at, the year. */
  readonly data: Record<string, RpmPersonData>;
  readonly loading: boolean;
  readonly error: string | null;
  /** A write is in flight. */
  readonly saving: boolean;
  /** The last refused write's message, until a form opens or a write succeeds. */
  readonly saveError: string | null;
}

const initialState: RpmState = { data: {}, loading: false, error: null, saving: false, saveError: null };

/** This person's data, or an empty book — a person with no plan yet is the normal case. */
function bookOf(state: RpmState, personaId: string): RpmPersonData {
  return state.data[personaId] ?? { items: [], categories: [], grassItems: [], week: null, summary: null };
}

/**
 * Replace one person's data through one function. Every write answer comes
 * through here, so every one of them ends the in-flight state and clears the
 * last refusal (the write worked).
 */
function withBook(state: RpmState, personaId: string, change: (book: RpmPersonData) => RpmPersonData): RpmState {
  return { ...state, saving: false, saveError: null, data: { ...state.data, [personaId]: change(bookOf(state, personaId)) } };
}

/** Put an answered block in place, or last when it is new (adds and promotions are placed last). */
function upsertItem(items: RpmItem[], item: RpmItem): RpmItem[] {
  return items.some((i) => i.id === item.id) ? items.map((i) => (i.id === item.id ? item : i)) : [...items, item];
}

/** Append a note to one block wherever it is shown: on the plan and in the week being looked at (D9). */
function withNote(items: RpmItem[], itemId: string, note: RpmItem['notes'][number]): RpmItem[] {
  return items.map((i) => (i.id === itemId ? { ...i, notes: [...i.notes, note] } : i));
}

export const rpmFeature = createFeature({
  name: 'rpm',
  reducer: createReducer(
    initialState,
    on(RpmPageActions.entered, (state) => ({ ...state, loading: true, error: null })),
    on(RpmApiActions.loadSuccess, (state, { personaId, data }) => ({
      ...state,
      loading: false,
      data: { ...state.data, [personaId]: data },
    })),
    on(RpmApiActions.loadFailure, (state, { error }) => ({ ...state, loading: false, error })),
    on(RpmPageActions.formOpened, (state) => ({ ...state, saveError: null })),

    // Every write starts the in-flight state; its answer (withBook) or refusal ends it.
    on(
      RpmPageActions.itemAdded,
      RpmPageActions.itemEdited,
      RpmPageActions.itemDeleted,
      RpmPageActions.statusSet,
      RpmPageActions.actionTicked,
      RpmPageActions.planReordered,
      RpmPageActions.noteAdded,
      RpmPageActions.grassPromoted,
      RpmPageActions.reviewSaved,
      RpmPageActions.categoryAdded,
      RpmPageActions.categoryEdited,
      RpmPageActions.grassCaptured,
      RpmPageActions.grassDeleted,
      (state) => ({ ...state, saving: true }),
    ),
    on(RpmApiActions.writeFailure, (state, { error }) => ({ ...state, saving: false, saveError: error })),

    // Reads that refresh what the server derives. They do not end a write.
    on(RpmApiActions.weekLoaded, (state, { personaId, week }) => ({
      ...state,
      data: { ...state.data, [personaId]: { ...bookOf(state, personaId), week } },
    })),
    on(RpmApiActions.summaryLoaded, (state, { personaId, summary }) => ({
      ...state,
      data: { ...state.data, [personaId]: { ...bookOf(state, personaId), summary } },
    })),

    on(RpmApiActions.itemSaved, (state, { personaId, item }) =>
      withBook(state, personaId, (b) => ({ ...b, items: upsertItem(b.items, item) })),
    ),
    on(RpmApiActions.itemRemoved, (state, { personaId, itemId }) =>
      withBook(state, personaId, (b) => ({ ...b, items: b.items.filter((i) => i.id !== itemId) })),
    ),
    on(RpmApiActions.planOrdered, (state, { personaId, items, categories }) =>
      withBook(state, personaId, (b) => ({ ...b, items, categories })),
    ),
    on(RpmApiActions.noteSaved, (state, { personaId, itemId, note }) =>
      withBook(state, personaId, (b) => ({
        ...b,
        items: withNote(b.items, itemId, note),
        week: b.week ? { ...b.week, items: withNote(b.week.items, itemId, note) } : null,
      })),
    ),
    on(RpmApiActions.grassPromoted, (state, { personaId, grassItemId, item }) =>
      withBook(state, personaId, (b) => ({
        ...b,
        items: upsertItem(b.items, item),
        grassItems: b.grassItems.filter((g) => g.id !== grassItemId),
      })),
    ),
    on(RpmApiActions.reviewSaved, (state, { personaId, weekStartDt, review }) =>
      withBook(state, personaId, (b) => ({
        ...b,
        week: b.week && b.week.weekStartDt === weekStartDt ? { ...b.week, review } : b.week,
      })),
    ),
    on(RpmApiActions.categorySaved, (state, { personaId, category }) =>
      withBook(state, personaId, (b) => ({
        ...b,
        categories: b.categories.some((c) => c.id === category.id)
          ? b.categories.map((c) => (c.id === category.id ? category : c))
          : [...b.categories, category],
      })),
    ),
    on(RpmApiActions.grassCaptured, (state, { personaId, item }) =>
      withBook(state, personaId, (b) => ({ ...b, grassItems: [...b.grassItems, item] })),
    ),
    on(RpmApiActions.grassDeleted, (state, { personaId, grassItemId }) =>
      withBook(state, personaId, (b) => ({ ...b, grassItems: b.grassItems.filter((g) => g.id !== grassItemId) })),
    ),
  ),
});

export const { selectData: selectRpmData, selectSaving: selectRpmSaving, selectSaveError: selectRpmSaveError } = rpmFeature;
